/**
 * Rascunho de mensagem por protocolo: UMA chave de localStorage por `user_id`,
 * com o dict inteiro (`protocol_id -> Draft`) dentro. Mesmo desenho de
 * `prefs/store.ts` e `prefs/recentIcons.ts`, e FORA do dict de prefs de
 * proposito: la o valor e string de ate 64 chars filtrada por allowlist e
 * `writePref` LANCA no que nao passa — texto livre quebra os dois.
 *
 * localStorage e nao Dexie porque a leitura precisa ser SINCRONA: com IO
 * assincrono o composer montaria vazio e o texto apareceria depois.
 */

export interface Draft {
  text: string
  /** wamid da mensagem marcada quando o rascunho foi gravado. */
  replyToWamid?: string
  /** epoch em MILISSEGUNDOS (`Date.now()`). `IChatInfo.last_message.timestamp`
   *  e em SEGUNDOS — nao comparar os dois sem converter. */
  updatedAt: number
}

/** `protocol_id -> Draft`. O que sai de `readDrafts` vem CONGELADO — a identidade
 *  e compartilhada, entao mutar vazaria rascunho entre usuarios (e lanca). */
export type Drafts = Record<string, Draft>

/** Rascunhos mudaram NESTA aba — quem depende re-le. */
export const DRAFTS_CHANGE_EVENT = 'drafts-change'

/**
 * Um protocolo teve o rascunho MORTO nesta aba (`detail.protocolId`). Evento
 * separado do de mudanca de proposito: e INTENCAO, nao delta de estado — sai
 * mesmo quando nao havia nada gravado, porque quem tem o composer aberto pode
 * estar com o debounce em voo e precisa desistir do flush do unmount.
 */
export const DRAFT_REMOVED_EVENT = 'draft-removed'

/**
 * A chave INTEIRA de um usuario foi apagada (`detail.userId`) — saida de sessao.
 * Como o `DRAFT_REMOVED_EVENT`, e INTENCAO e nao delta de estado: quem tem
 * composer aberto precisa desistir do flush do unmount, inclusive quando o
 * texto digitado nunca chegou a ser gravado. Sem este aviso o logout apagaria o
 * rascunho e o unmount seguinte o regravaria em claro.
 */
export const DRAFTS_CLEARED_EVENT = 'drafts-cleared'

/** Guardas de STORAGE, nao limites da API do WhatsApp (nao verificado no back). */
export const MAX_DRAFT_LENGTH = 4096
export const MAX_DRAFT_ENTRIES = 200
export const DRAFT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

export function draftsKey(userId: string): string {
  return `drafts:${userId}`
}

function safeStorage(): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

/** String crua da chave, ou null (sem storage / getItem que lanca). */
function rawItem(userId: string): string | null {
  const storage = safeStorage()
  if (!storage) return null
  try {
    return storage.getItem(draftsKey(userId))
  } catch {
    return null
  }
}

/**
 * Fallback de sessao: quando o setItem falha (cota/modo restrito) o rascunho
 * vale ate o reload em vez de sumir sem aviso. Sai do mapa assim que um write
 * volta a persistir — memoria nunca mascara storage saudavel.
 */
const memory = new Map<string, Drafts>()

/**
 * Objeto vazio COMPARTILHADO: identidade estavel pro caso "nao tem nada".
 * CONGELADO porque a identidade e compartilhada entre TODOS os usuarios — um
 * unico `readDrafts('u1')['p'] = ...` vazaria rascunho pro `readDrafts('u2')`,
 * e nem `__resetDraftsMemory()` desfaria. Devolver `{}` novo a cada leitura
 * resolveria o vazamento e quebraria a memoizacao (ver `cache`), entao a saida
 * e congelar.
 */
const VAZIO: Drafts = Object.freeze<Drafts>({})

/**
 * Snapshot memoizado. `useSyncExternalStore` compara por `Object.is`, entao um
 * objeto novo a cada chamada e loop infinito de render — a mesma armadilha que
 * fez `usePref` devolver string em vez do dict. Invalidado pelo conteudo CRU do
 * storage (cobre escrita de outra aba) E por um contador de escrita local
 * (cobre o fallback em memoria, que nao tem string pra comparar).
 */
let versao = 0
let cache: { userId: string; raw: string | null; versao: number; value: Drafts } | null = null

/** So pra teste: zera o fallback de sessao e o snapshot memoizado. */
export function __resetDraftsMemory(): void {
  memory.clear()
  cache = null
  versao++
}

function isDraft(value: unknown): value is Draft {
  if (typeof value !== 'object' || value === null) return false
  const candidato = value as Record<string, unknown>
  if (typeof candidato.text !== 'string') return false
  if (typeof candidato.updatedAt !== 'number') return false
  return candidato.replyToWamid === undefined || typeof candidato.replyToWamid === 'string'
}

function isDrafts(value: unknown): value is Drafts {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false
  return Object.values(value as Record<string, unknown>).every(isDraft)
}

/**
 * Poda na LEITURA, sem regravar (mesma escolha de `recentIcons.readRecentIcons`):
 * ler nao e efeito colateral. Quem persiste a limpeza e o proximo write.
 * Protocolo pode sumir sem evento nenhum — este e o teto de crescimento.
 */
function podar(drafts: Drafts): Drafts {
  const limite = Date.now() - DRAFT_MAX_AGE_MS
  const saida: Drafts = {}
  for (const [protocolId, draft] of Object.entries(drafts)) {
    if (draft.updatedAt >= limite) saida[protocolId] = Object.freeze(draft)
  }
  // Congelado junto com as entradas: este e o unico ponto onde o snapshot
  // nasce, e ele so roda em cache miss (escrita ou troca de conteudo cru) —
  // leitura repetida nao paga nada. Freeze e raso, dai as entradas tambem.
  return Object.freeze(saida)
}

function computeDrafts(userId: string, raw: string | null): Drafts {
  const emMemoria = memory.get(userId)
  if (emMemoria) return podar(emMemoria)
  if (!raw) return VAZIO
  try {
    const parsed: unknown = JSON.parse(raw)
    return isDrafts(parsed) ? podar(parsed) : VAZIO
  } catch {
    return VAZIO
  }
}

/**
 * Leitura total e defensiva: qualquer falha ou forma inesperada devolve `{}`.
 * IDENTIDADE ESTAVEL entre chamadas enquanto nada mudar, e o retorno vem
 * CONGELADO (mapa e entradas) — mutar lanca `TypeError` em vez de vazar.
 */
export function readDrafts(userId: string | null | undefined): Drafts {
  if (!userId) return VAZIO
  const raw = rawItem(userId)
  if (cache && cache.userId === userId && cache.raw === raw && cache.versao === versao) {
    return cache.value
  }
  const value = computeDrafts(userId, raw)
  cache = { userId, raw, versao, value }
  return value
}

export function readDraft(
  userId: string | null | undefined,
  protocolId: string,
): Draft | null {
  return readDrafts(userId)[protocolId] ?? null
}

function persist(userId: string, next: Drafts): void {
  versao++
  const storage = safeStorage()
  if (!storage) {
    memory.set(userId, next)
    return
  }
  try {
    storage.setItem(draftsKey(userId), JSON.stringify(next))
    memory.delete(userId)
  } catch {
    memory.set(userId, next)
  }
}

function notify(): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(DRAFTS_CHANGE_EVENT))
}

/** Despacho SINCRONO (`dispatchEvent`): quem apaga sabe, dentro da propria
 *  chamada, que o aviso ja rodou — e o que permite distinguir "fui eu" de
 *  "foi por fora" sem depender do ciclo de render do React. */
function announceRemoval(protocolId: string): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(DRAFT_REMOVED_EVENT, { detail: { protocolId } }))
}

/** Tambem SINCRONO, e pelo mesmo motivo: quem chama `clearDrafts` esta prestes a
 *  desmontar a arvore, entao o aviso tem que chegar antes do unmount. */
function announceClear(userId: string): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(DRAFTS_CLEARED_EVENT, { detail: { userId } }))
}

/** Corta as entradas mais antigas por `updatedAt` ate caber no teto. */
function capEntries(drafts: Drafts): Drafts {
  const entradas = Object.entries(drafts)
  if (entradas.length <= MAX_DRAFT_ENTRIES) return drafts
  entradas.sort(([, a], [, b]) => b.updatedAt - a.updatedAt)
  return Object.fromEntries(entradas.slice(0, MAX_DRAFT_ENTRIES))
}

/**
 * Grava o rascunho do protocolo. Sem `user_id` (bootstrap antes do /auth/me) e
 * no-op: `drafts:undefined` seria lixo que ninguem le. NUNCA lanca — falha de
 * cota cai no mapa em memoria.
 */
export function writeDraft(
  userId: string | null | undefined,
  protocolId: string,
  draft: Omit<Draft, 'updatedAt'>,
): void {
  if (!userId || !protocolId) return
  const texto = draft.text.slice(0, MAX_DRAFT_LENGTH)
  const atual = readDrafts(userId)
  const proximo: Drafts = {
    ...atual,
    [protocolId]: {
      text: texto,
      ...(draft.replyToWamid ? { replyToWamid: draft.replyToWamid } : {}),
      updatedAt: Date.now(),
    },
  }
  persist(userId, capEntries(proximo))
  notify()
}

/**
 * Remocao idempotente: chave ausente nao grava nem emite MUDANCA (evitaria
 * releitura a toa). O anuncio de remocao sai sempre, porem — o composer aberto
 * pode ter texto digitado e nenhuma gravacao ainda, e e justamente ele que
 * regravaria o rascunho no flush do unmount se nao soubesse da morte.
 */
export function removeDraft(
  userId: string | null | undefined,
  protocolId: string,
): void {
  if (!userId) return
  const atual = readDrafts(userId)
  if (protocolId in atual) {
    const proximo: Drafts = { ...atual }
    delete proximo[protocolId]
    persist(userId, proximo)
    notify()
  }
  announceRemoval(protocolId)
}

/**
 * Apaga TODOS os rascunhos de um usuario — a saida da sessao (logout, login de
 * outro, troca de usuario detectada pelo socket). Rascunho e conteudo de
 * conversa em CLARO no localStorage, a mesma classe de dado que o cache de
 * conversa cifra (`lib/cache/crypto.ts`) e joga fora no logout; deixar aqui e
 * entregar texto do atendente A pro atendente B na mesma estacao.
 *
 * Escopado pela CHAVE do usuario que sai: nunca `localStorage.clear()`, que
 * levaria rascunho de quem nao esta saindo (e as prefs, que sobrevivem de
 * proposito).
 */
export function clearDrafts(userId: string | null | undefined): void {
  if (!userId) return
  versao++
  memory.delete(userId)
  const storage = safeStorage()
  if (storage) {
    try {
      storage.removeItem(draftsKey(userId))
    } catch {
      // Storage indisponivel: o `memory.delete` acima ja tirou o que existia.
    }
  }
  announceClear(userId)
  notify()
}

/**
 * Assina a limpeza de sessao. Recebe o `user_id` do dono que saiu — quem tem
 * composer aberto compara com o proprio dono antes de desistir de gravar, pra
 * limpeza de OUTRO usuario nao abandonar o rascunho de quem esta na tela.
 */
export function subscribeDraftsCleared(
  onCleared: (userId: string) => void,
): () => void {
  if (typeof window === 'undefined') return () => {}
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<{ userId?: unknown }>).detail
    if (typeof detail?.userId === 'string') onCleared(detail.userId)
  }
  window.addEventListener(DRAFTS_CLEARED_EVENT, handler)
  return () => window.removeEventListener(DRAFTS_CLEARED_EVENT, handler)
}

/**
 * Assina o anuncio de morte de rascunho desta aba. So `DRAFT_REMOVED_EVENT` —
 * o `storage` de outra aba nao carrega qual protocolo saiu, e quem depende do
 * ESTADO ja tem `subscribeDrafts`.
 */
export function subscribeDraftRemoved(
  onRemoved: (protocolId: string) => void,
): () => void {
  if (typeof window === 'undefined') return () => {}
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<{ protocolId?: unknown }>).detail
    if (typeof detail?.protocolId === 'string') onRemoved(detail.protocolId)
  }
  window.addEventListener(DRAFT_REMOVED_EVENT, handler)
  return () => window.removeEventListener(DRAFT_REMOVED_EVENT, handler)
}

/** `storage` cobre outra aba; o CustomEvent cobre esta. Devolve o unsubscribe. */
export function subscribeDrafts(onChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(DRAFTS_CHANGE_EVENT, onChange)
  window.addEventListener('storage', onChange)
  return () => {
    window.removeEventListener(DRAFTS_CHANGE_EVENT, onChange)
    window.removeEventListener('storage', onChange)
  }
}
